/**
 * Browser half of the API tunnel protocol (WebCrypto, no dependencies).
 *
 * Must agree byte for byte with backend/src/utils/tunnelCrypto.js — that file
 * has the full wire format. In short:
 *
 *   request  = ver | kid | client pubkey(65) | nonce(12) | AES-256-GCM(kReq) ct+tag
 *   response = ver | nonce(12) | AES-256-GCM(kRes) ct+tag, AAD = ver | request nonce
 *   kReq/kRes = HKDF-SHA256(ECDH(client, server), salt = client pubkey, "sh0p-tunnel-v1 req|res")
 *   plaintext = flag(0 plain | 1 deflate-raw) | UTF-8 JSON
 */

const VERSION = 1;
const NONCE_BYTES = 12;
const COMPRESS_OVER_BYTES = 1024;
const FLAG_PLAIN = 0;
const FLAG_DEFLATE = 1;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const concat = (...parts) => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  parts.forEach((p) => {
    out.set(p, offset);
    offset += p.length;
  });
  return out;
};

export const base64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const bytesToBase64Url = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

export const randomNonceString = () => bytesToBase64Url(crypto.getRandomValues(new Uint8Array(16)));

const streamSupports = (Ctor) => {
  try {
    // Constructing throws on an unsupported format (Safari < 16.4 has no deflate-raw).
    return typeof Ctor === "function" && Boolean(new Ctor("deflate-raw"));
  } catch {
    return false;
  }
};

export const canCompress = () =>
  typeof CompressionStream !== "undefined" && streamSupports(CompressionStream);
export const canDecompress = () =>
  typeof DecompressionStream !== "undefined" && streamSupports(DecompressionStream);

const pipeThrough = async (bytes, stream) =>
  new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer());

const encodePayload = async (value) => {
  const json = encoder.encode(JSON.stringify(value));
  if (json.length > COMPRESS_OVER_BYTES && canCompress()) {
    const deflated = await pipeThrough(json, new CompressionStream("deflate-raw"));
    return concat(Uint8Array.of(FLAG_DEFLATE), deflated);
  }
  return concat(Uint8Array.of(FLAG_PLAIN), json);
};

const decodePayload = async (plaintext) => {
  const flag = plaintext[0];
  let json = plaintext.subarray(1);
  if (flag === FLAG_DEFLATE) {
    json = await pipeThrough(json, new DecompressionStream("deflate-raw"));
  } else if (flag !== FLAG_PLAIN) {
    throw new Error("tunnel: bad payload flag");
  }
  return JSON.parse(decoder.decode(json));
};

const hkdfKey = (baseKey, salt, info, usage) =>
  crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: encoder.encode(info) },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    [usage],
  );

/**
 * One ephemeral ECDH key per page load, reused for every request: the server
 * caches the derived keys per client key, so this is one ECDH per session on
 * each side rather than one per request.
 */
export const createSession = async ({ kid, publicKey }) => {
  const serverKey = await crypto.subtle.importKey(
    "raw",
    publicKey,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, [
    "deriveBits",
  ]);
  const clientPub = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const shared = await crypto.subtle.deriveBits(
    { name: "ECDH", public: serverKey },
    pair.privateKey,
    256,
  );
  const hkdf = await crypto.subtle.importKey("raw", shared, "HKDF", false, ["deriveKey"]);
  const kReq = await hkdfKey(hkdf, clientPub, "sh0p-tunnel-v1 req", "encrypt");
  const kRes = await hkdfKey(hkdf, clientPub, "sh0p-tunnel-v1 res", "decrypt");
  const aad = concat(Uint8Array.of(VERSION, kid), clientPub);

  return {
    async sealRequest(envelope) {
      const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
      const sealed = new Uint8Array(
        await crypto.subtle.encrypt(
          { name: "AES-GCM", iv: nonce, additionalData: aad, tagLength: 128 },
          kReq,
          await encodePayload(envelope),
        ),
      );
      return { body: concat(aad, nonce, sealed), nonce };
    },

    async openResponse(buffer, requestNonce) {
      const body = new Uint8Array(buffer);
      if (body.length < 1 + NONCE_BYTES + 17 || body[0] !== VERSION) {
        throw new Error("tunnel: bad response frame");
      }
      const plaintext = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: body.subarray(1, 1 + NONCE_BYTES),
          additionalData: concat(Uint8Array.of(VERSION), requestNonce),
          tagLength: 128,
        },
        kRes,
        body.subarray(1 + NONCE_BYTES),
      );
      return decodePayload(new Uint8Array(plaintext));
    },
  };
};
